/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Alert, Button, Heading, Stack, Text} from '@chakra-ui/react'
import {AlertIcon, BrandLogoBlack} from '../icons'
import PasswordlessLogin from '../passwordless-login'
import StandardLogin from '../standard-login'

const LoginForm = ({
    submitForm,
    clickForgotPassword,
    handleForgotPasswordClick = clickForgotPassword,
    handlePasswordlessLoginClick,
    clickCreateAccount,
    form,
    variant,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    setLoginType
}) => {
    const {formatMessage} = useIntl()
    return (
        <Fragment>
            {variant !== 'page' && (
                <Stack justify="center" align="center" spacing={8} marginBottom={8}>
                    <BrandLogoBlack
                        width={'10rem'}
                        height="auto"
                        aria-label={formatMessage({
                            defaultMessage: 'STOKKE Logo',
                            id: 'global.brand_logo'
                        })}
                    />
                    <Heading align="center" fontSize="xl" fontWeight="semibold">
                        <FormattedMessage
                            defaultMessage="Welcome Back"
                            id="login_form.message.welcome_back"
                        />
                    </Heading>
                </Stack>
            )}

            <form
                id="login-form"
                noValidate
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form"
            >
                <Stack spacing={8} paddingLeft={4} paddingRight={4}>
                    {form.formState.errors.global && (
                        <Alert status="error">
                            <AlertIcon color="red.500" boxSize={4} />
                            <Text fontSize="sm" ml={3}>
                                {form.formState.errors.global.message}
                            </Text>
                        </Alert>
                    )}
                    {isPasswordlessEnabled ? (
                        <PasswordlessLogin
                            form={form}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            handlePasswordlessLoginClick={handlePasswordlessLoginClick}
                            isSocialEnabled={isSocialEnabled}
                            idps={idps}
                            setLoginType={setLoginType}
                        />
                    ) : (
                        <StandardLogin
                            form={form}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            isSocialEnabled={isSocialEnabled}
                            idps={idps}
                        />
                    )}
                    <Stack spacing={6} marginTop={-2}>
                        <Stack direction="row" spacing={1} justify="center">
                            <Text fontSize="sm">
                                <FormattedMessage
                                    defaultMessage="Don't have an account?"
                                    id="login_form.message.dont_have_account"
                                />
                            </Text>
                            <Button variant="link" size="sm" onClick={clickCreateAccount}>
                                <FormattedMessage
                                    defaultMessage="Create account"
                                    id="login_form.action.create_account"
                                />
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

LoginForm.propTypes = {
    submitForm: PropTypes.func,
    clickCreateAccount: PropTypes.func,
    clickForgotPassword: PropTypes.func,
    handleForgotPasswordClick: PropTypes.func,
    handlePasswordlessLoginClick: PropTypes.func,
    form: PropTypes.object,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string),
    setLoginType: PropTypes.func,
    variant: PropTypes.string
}

export default LoginForm
